import { Badge, Button, Group, Select, Text } from "@mantine/core";
import { IconPlus, IconTags } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import { DataTable, FilterBar, PageHeader, useSearchParamState } from "@how/ui";
import { useCampusOptions } from "../groups/group-queries";
import { EventCategoriesModal } from "./EventCategoriesModal";
import { EventDrawer } from "./EventDrawer";
import { EventModal } from "./EventModal";
import {
  REGISTRATION_LABEL,
  VISIBILITY_LABEL,
  formatWhen,
  useEventCategories,
  useEvents,
  type EventRow,
} from "./event-queries";
import { semanticColor, type SemanticColor } from "@how/design-system";

const VISIBILITY_COLOR: Record<EventRow["visibility"], SemanticColor> = {
  public: semanticColor("success"),
  unlisted: semanticColor("neutral"),
  private: semanticColor("special"),
};

const columns: ColumnDef<EventRow>[] = [
  {
    accessorKey: "title",
    header: "Event",
    enableSorting: false,
    cell: (ctx) => (
      <div>
        <Text size="sm" fw={600}>
          {ctx.row.original.title}
        </Text>
        <Text size="xs" c="dimmed">
          {[ctx.row.original.categoryName, ctx.row.original.ministry]
            .filter((p) => p !== null)
            .join(" · ")}
        </Text>
      </div>
    ),
  },
  {
    accessorKey: "nextAt",
    header: "Next",
    enableSorting: false,
    cell: (ctx) => {
      const e = ctx.row.original;
      return e.nextAt === null ? (
        <Text size="sm" c="dimmed">
          Nothing ahead
        </Text>
      ) : (
        <div>
          <Text size="sm">{formatWhen(e.nextAt, e.campusTimeZone)}</Text>
          {e.upcomingCount > 1 ? (
            <Text size="xs" c="dimmed">
              {String(e.upcomingCount)} dates ahead
            </Text>
          ) : null}
        </div>
      );
    },
  },
  {
    accessorKey: "location",
    header: "Where",
    enableSorting: false,
    cell: (ctx) => (
      <div>
        <Text size="sm">{ctx.row.original.location ?? "—"}</Text>
        {ctx.row.original.campusName === null ? null : (
          <Text size="xs" c="dimmed">
            {ctx.row.original.campusName}
          </Text>
        )}
      </div>
    ),
  },
  {
    accessorKey: "registration",
    header: "Registration",
    enableSorting: false,
    cell: (ctx) => (
      <Text size="sm">
        {REGISTRATION_LABEL[ctx.row.original.registration]}
        {ctx.row.original.capacity === null ? "" : ` · up to ${String(ctx.row.original.capacity)}`}
      </Text>
    ),
  },
  {
    accessorKey: "visibility",
    header: "Visibility",
    enableSorting: false,
    cell: (ctx) => (
      <Badge variant="light" color={VISIBILITY_COLOR[ctx.row.original.visibility]}>
        {VISIBILITY_LABEL[ctx.row.original.visibility]}
      </Badge>
    ),
  },
  {
    accessorKey: "isActive",
    header: "Status",
    enableSorting: false,
    cell: (ctx) =>
      ctx.row.original.isActive ? (
        <Badge variant="light" color="teal">
          Active
        </Badge>
      ) : (
        <Badge variant="light" color="gray">
          Archived
        </Badge>
      ),
  },
];

const PAGE = { pageIndex: 0, pageSize: 200 };
const WHEN_OPTIONS = [
  { value: "upcoming", label: "With dates ahead" },
  { value: "all", label: "Every event" },
];
const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "Active and archived" },
];

/**
 * The church's events (ADR 0041): what is on, soonest first. A row opens the record with its dates and, per date,
 * who is registered and who came. Adding, editing and archiving take events.manage; the roster actions take
 * registrations.manage; the database decides both.
 */
export function EventsPage() {
  const canManage = usePermission("events.manage", { write: true });
  const categories = useEventCategories();
  const campuses = useCampusOptions();
  const [search, setSearch] = useSearchParamState("q", "");
  const [categoryId, setCategoryId] = useSearchParamState("category", "");
  const [campusId, setCampusId] = useSearchParamState("campus", "");
  const [when, setWhen] = useSearchParamState("when", "upcoming");
  const [status, setStatus] = useSearchParamState("status", "active");
  const [selected, setSelected] = useSearchParamState("event", "");
  const [adding, setAdding] = useState(false);
  const [managingCategories, setManagingCategories] = useState(false);

  const events = useEvents({
    search,
    ...(categoryId === "" ? {} : { categoryId }),
    ...(campusId === "" ? {} : { campusId }),
    status: status === "archived" || status === "all" ? status : "active",
    when: when === "all" ? "all" : "upcoming",
  });
  const tableStatus = events.isPending ? "loading" : events.isError ? "error" : "ready";

  return (
    <>
      <PageHeader
        title="Events"
        description="What is on, soonest first. A date carries its own registrations and attendance."
        actions={
          canManage.data === true ? (
            <Group gap="sm">
              <Button
                variant="light"
                leftSection={<IconTags size={16} />}
                onClick={() => {
                  setManagingCategories(true);
                }}
              >
                Categories
              </Button>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => {
                  setAdding(true);
                }}
              >
                Add event
              </Button>
            </Group>
          ) : undefined
        }
      />
      <FilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by title"
        filters={
          <>
            <Select
              aria-label="Filter by category"
              placeholder="Any category"
              clearable
              data={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
              value={categoryId === "" ? null : categoryId}
              onChange={(value) => {
                setCategoryId(value ?? undefined);
              }}
              w={200}
            />
            <Select
              aria-label="Filter by campus"
              placeholder="Any campus"
              clearable
              data={campuses.data ?? []}
              value={campusId === "" ? null : campusId}
              onChange={(value) => {
                setCampusId(value ?? undefined);
              }}
              w={200}
            />
            <Select
              aria-label="Filter by dates"
              data={WHEN_OPTIONS}
              value={when}
              allowDeselect={false}
              onChange={(value) => {
                setWhen(value === null || value === "upcoming" ? undefined : value);
              }}
              w={180}
            />
            <Select
              aria-label="Filter by status"
              data={STATUS_OPTIONS}
              value={status}
              allowDeselect={false}
              onChange={(value) => {
                setStatus(value === null || value === "active" ? undefined : value);
              }}
              w={190}
            />
          </>
        }
      />
      <DataTable
        label="Events"
        columns={columns}
        rows={events.data ?? []}
        totalRows={events.data?.length ?? 0}
        status={tableStatus}
        pagination={PAGE}
        onPaginationChange={() => undefined}
        sorting={[]}
        onSortingChange={() => undefined}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          setSelected(row.id);
        }}
        emptyTitle={when === "all" ? "No events" : "Nothing ahead"}
        emptyDescription={
          when === "all"
            ? "Add the first event, or show archived ones."
            : "Every event has had its last date. Add dates to one, or show every event."
        }
        errorMessage="Could not load events."
        onRetry={() => {
          void events.refetch();
        }}
      />
      <EventDrawer
        eventId={selected === "" ? null : selected}
        onClose={() => {
          setSelected(undefined);
        }}
      />
      <EventModal
        key={adding ? "new" : "closed"}
        event={adding ? null : undefined}
        canManage={canManage.data === true}
        onClose={() => {
          setAdding(false);
        }}
        onCreated={(id) => {
          setAdding(false);
          setSelected(id);
        }}
      />
      <EventCategoriesModal
        opened={managingCategories}
        onClose={() => {
          setManagingCategories(false);
        }}
      />
    </>
  );
}
